import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  SkillEvent_Vfx,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Wait,
  SkillMomentum,
  SkillEvent_ParticleEmission,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { FRONTAL_WAVE_3_5_PATTERN } from "../skill-patterns/frontal-wave-3-5";
import { CONE_3_PATTERN } from "../skill-patterns/cone-3";
import { CONE_5_PATTERN } from "../skill-patterns/cone-5";
import { msToTicks } from "../utils/ms-to-ticks";

// Frontal Beams (WORK IN PROGRESS)
export const [
  FLAMETHROWER,
  BRINE,
  SOLAR_BEAM,
  ZAP_CANNON,
  TOXIC_SPIKES,
  PIN_MISSILE,
  ROCK_BLAST,
  LASH_OUT,
  STEEL_BEAM,
  HYPER_VOICE,
] = [
  {
    id: "flamethrower",
    description: "Fires a stream of fire. Casting becomes faster over time.",
    pokemon_type: PokemonType.FIRE,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(7000),
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 45,
    particle_speed: 500,
    max_spawn_delay_ms: 600,
    particle_sprite_path: "/assets/spells/particle/fire-1.png",
    particle_sprite_scale: 1.5,
    anim_loops: 10,
    anim_speed: 2,
    should_align_to_destination: false,
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
  },
  {
    id: "brine",
    description: "Fires a stream of salt water at enemies.",
    pokemon_type: PokemonType.WATER,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 125,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 35,
    particle_speed: 500,
    max_spawn_delay_ms: 600,
    particle_sprite_path: "/assets/spells/particle/water-1.png",
    particle_sprite_scale: 1.75,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "solar-beam",
    description:
      "Fires a beam of concentrated solar energy. 15% bonus damage if there is a FIRE-type ally on the team.",
    pokemon_type: PokemonType.GRASS,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 110,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 25,
    particle_speed: 500,
    max_spawn_delay_ms: 150,
    particle_sprite_path: "/assets/spells/projectile/slash-yellow.png",
    particle_sprite_scale: 1.2,
    anim_loops: 10,
    anim_speed: 1,
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FIRE,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.15,
        },
      },
    ] as SkillData["team_conditions"],
  },
  {
    id: "zap-cannon",
    description: "Fires a powerful blast of electricity in a frontal wave.",
    pokemon_type: PokemonType.ELECTRIC,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 100,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 25,
    particle_speed: 500,
    max_spawn_delay_ms: 600,
    particle_sprite_path: "/assets/spells/particle/electric-1.png",
    particle_sprite_scale: 1,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "toxic-spikes",
    description: "Scatters several toxic spikes in a cone in front.",
    pokemon_type: PokemonType.POISON,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 125,
    range: 3,
    align_to_user_dir: true,
    cast_pattern: CONE_3_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 45,
    particle_speed: 500,
    max_spawn_delay_ms: 633,
    particle_sprite_path: "/assets/spells/particle/poison-1.png",
    particle_sprite_scale: 1.7,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "pin-missile",
    description: "Fires multiple sharp pins at enemies.",
    pokemon_type: PokemonType.BUG,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 120,
    range: 3,
    align_to_user_dir: true,
    cast_pattern: CONE_3_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 3,
    particle_count: 45,
    particle_speed: 500,
    max_spawn_delay_ms: 633,
    particle_sprite_path: "/assets/spells/particle/bug-1.png",
    particle_sprite_scale: 1.7,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "rock-blast",
    description: "Fires a blast of rocks at enemies.",

    pokemon_type: PokemonType.ROCK,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 125,
    range: 3,
    align_to_user_dir: true,
    cast_pattern: CONE_3_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 25,
    particle_speed: 500,
    max_spawn_delay_ms: 633,
    particle_sprite_path: "/assets/spells/particle/rock-1.png",
    particle_sprite_scale: 1.5,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "lash-out",
    description: "Strikes enemies in a frontal wave with a dark lash.",
    pokemon_type: PokemonType.DARK,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 125,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 10,
    particle_speed: 500,
    max_spawn_delay_ms: 633,
    particle_sprite_path: "/assets/spells/projectile/slash-black.png",
    particle_sprite_scale: 1.5,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "steel-beam",
    description: "Fires a beam of concentrated steel energy.",

    pokemon_type: PokemonType.STEEL,
    types: [SkillType.DAMAGE],
    cooldown_ticks: 125,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 10,
    particle_speed: 500,
    max_spawn_delay_ms: 633,
    particle_sprite_path: "/assets/spells/projectile/slash-white.png",
    particle_sprite_scale: 1.5,
    anim_loops: 10,
    anim_speed: 1,
  },
  {
    id: "hyper-voice",
    description:
      "A loud shout that damages enemies. Cooldown is reduced by 5% per use, up to 30% reduction.",
    pokemon_type: PokemonType.FAIRY,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(7000),
    range: 5,
    align_to_user_dir: false,
    cast_pattern: CONE_5_PATTERN(),
    invoke_duration_ticks: 10,
    hit_count: 2,
    particle_count: 15,
    particle_speed: 400,
    max_spawn_delay_ms: 633,
    particle_sprite_path:
      "/assets/spells/particle/bell_ring_and_sounds_004__18.png",
    particle_sprite_scale: 1.5,
    anim_loops: 10,
    anim_speed: 1,
    momentum: {
      max_stacks: 6,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description || "",
  power: 1,
  pokemon_type: skill.pokemon_type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: skill.range,
  },
  cast_pattern: skill.cast_pattern,
  align_to_user_dir: true,
  charging: {
    is_required: true,
    align_to: "target",
    duration_ticks: 35,
  } as const,
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-white-1.png",
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/self_sparkles_006__50.png",
      speed: 1.25,
      sprite_scale: 2,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    ...Array.from({ length: skill.hit_count ?? 1 })
      .map((_, index) => [
        {
          __typename: "ParticleEmission",
          duration_ticks: 10,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          particle_count: skill.particle_count ?? 30,
          particle_speed: skill.particle_speed ?? 500,
          max_spawn_delay_ms: skill.max_spawn_delay_ms ?? 150,
          pattern: skill.cast_pattern,
          sprite_path:
            skill.particle_sprite_path ?? "/assets/spells/particle/pin.png",
          sprite_scale: skill.particle_sprite_scale ?? 1,
          anim_loops: skill.anim_loops ?? 10,
          anim_speed: skill.anim_speed ?? 1,
          should_align_pattern_to_user_dir: true,
          should_align_to_destination:
            skill.should_align_to_destination ?? true,
        } as SkillEvent_ParticleEmission,
        ...skill.cast_pattern.East.map(
          ([x, y, blocks]) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: 10,
              target: {
                type: "enemy",
                id: 0,
              },
              status_effects: [],
              starting_tick: 0,
              relative_x: x,
              relative_y: y,
              blocks_event_pipeline: true,
              damage: 1,
            }) as SkillEvent_AnchoredInvoke,
        ),
      ])
      .flat(),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
  ],
}));
